import React from "react";

import { CallDirection, CallStatus } from "../../types/calls.types";

import { DIRECTION_OPTIONS, STATUS_OPTIONS } from "./callsExplorer.constants";
import { useCallsExplorer } from "./callsExplorerContext";

// Local helpers to convert between the <input type="date"> value (YYYY-MM-DD)
// and the ISO start/end-of-day timestamps CallQueryParams.start_time/end_time
// expect.
const toStartOfDayIso = (date: string) => (date ? `${date}T00:00:00` : undefined);
const toEndOfDayIso = (date: string) => (date ? `${date}T23:59:59` : undefined);
const isoToDateInput = (iso?: string) => (iso ? iso.slice(0, 10) : "");

export const CallsFilterBar: React.FC = () => {
  const { pendingFilters, setPendingFilter, applyFilters, clearFilters, agents, agentsLoading } =
    useCallsExplorer();

  const handleApply = (e: React.FormEvent) => {
    e.preventDefault();
    applyFilters();
  };

  return (
    <form className="card shadow-sm border-0 mb-4" onSubmit={handleApply}>
      <div className="card-body">
        <div className="row g-3 align-items-end">
          <div className="col-md-2">
            <label htmlFor="calls-filter-status" className="form-label small fw-bold">
              Audio Status
            </label>
            <select
              id="calls-filter-status"
              className="form-select"
              value={pendingFilters.status ?? ""}
              onChange={(e) => {
                setPendingFilter("status", (e.target.value || undefined) as CallStatus | undefined);
              }}
            >
              <option value="">Any</option>
              {STATUS_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>

          <div className="col-md-2">
            <label htmlFor="calls-filter-direction" className="form-label small fw-bold">
              Direction
            </label>
            <select
              id="calls-filter-direction"
              className="form-select"
              value={pendingFilters.direction ?? ""}
              onChange={(e) => {
                setPendingFilter(
                  "direction",
                  (e.target.value || undefined) as CallDirection | undefined,
                );
              }}
            >
              <option value="">Any</option>
              {DIRECTION_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>

          <div className="col-md-3">
            <label htmlFor="calls-filter-agent" className="form-label small fw-bold">
              Agent
            </label>
            <input
              id="calls-filter-agent"
              className="form-control"
              list="calls-agents-datalist"
              placeholder={agentsLoading ? "Loading agents..." : "Type to search"}
              value={pendingFilters.calling_name ?? ""}
              onChange={(e) => {
                setPendingFilter("calling_name", e.target.value || undefined);
              }}
            />
            <datalist id="calls-agents-datalist">
              {agents.map((agent) => (
                <option key={agent.name} value={agent.name} />
              ))}
            </datalist>
          </div>

          <div className="col-md-2">
            <label htmlFor="calls-filter-called-number" className="form-label small fw-bold">
              Called number(s)
            </label>
            <input
              id="calls-filter-called-number"
              className="form-control"
              placeholder="e.g. 01234567890, 01234567891"
              value={pendingFilters.called_number ?? ""}
              onChange={(e) => {
                setPendingFilter("called_number", e.target.value || undefined);
              }}
            />
          </div>

          <div className="col-6 col-md-2">
            <label htmlFor="calls-filter-from" className="form-label small fw-bold">
              From
            </label>
            <input
              id="calls-filter-from"
              type="date"
              className="form-control"
              value={isoToDateInput(pendingFilters.start_time)}
              onChange={(e) => {
                setPendingFilter("start_time", toStartOfDayIso(e.target.value));
              }}
            />
          </div>

          <div className="col-6 col-md-2">
            <label htmlFor="calls-filter-to" className="form-label small fw-bold">
              To
            </label>
            <input
              id="calls-filter-to"
              type="date"
              className="form-control"
              value={isoToDateInput(pendingFilters.end_time)}
              onChange={(e) => {
                setPendingFilter("end_time", toEndOfDayIso(e.target.value));
              }}
            />
          </div>

          <div className="col-md-auto d-flex gap-2">
            <button type="submit" className="btn btn-primary fw-bold">
              Apply Filters
            </button>
            <button type="button" className="btn btn-outline-secondary" onClick={clearFilters}>
              Clear
            </button>
          </div>
        </div>

        <details className="mt-3 small">
          <summary
            className="fw-bold text-primary"
            style={{ cursor: "pointer", listStyle: "none" }}
          >
            What do the statuses mean?
          </summary>
          <div className="mt-2 text-muted">
            <p className="mb-2">
              Each call shows three badges - <strong>A</strong>udio, <strong>T</strong>ranscript and{" "}
              <strong>S</strong>ummary - coloured by how far along it is. Hover over one for its
              exact status.
            </p>
            <dl className="row mb-2">
              <dt className="col-sm-3 col-md-2">
                <span className="badge bg-success">Green</span>
              </dt>
              <dd className="col-sm-9 col-md-10">
                Ready. For audio, this may be straight from PBXware (provisional - a very recent
                call) or via the scheduled backup process; both play the same.
              </dd>

              <dt className="col-sm-3 col-md-2">
                <span className="badge bg-warning text-dark">Amber</span>
              </dt>
              <dd className="col-sm-9 col-md-10">
                In progress. Audio hasn&apos;t arrived yet, or the transcript / summary is being
                generated - recent calls usually take a minute or two.
              </dd>

              <dt className="col-sm-3 col-md-2">
                <span className="badge bg-danger">Red</span>
              </dt>
              <dd className="col-sm-9 col-md-10">
                The transcript or summary couldn&apos;t be generated.
              </dd>

              <dt className="col-sm-3 col-md-2">
                <span className="badge bg-light text-muted border">Grey</span>
              </dt>
              <dd className="col-sm-9 col-md-10">
                Not started yet. A transcript needs the audio first, and a summary needs the
                transcript.
              </dd>
            </dl>
            <p className="mb-0">
              The Audio Status filter only covers audio - you shouldn&apos;t normally need it, as
              audio now arrives for essentially every call.
            </p>
          </div>
        </details>
      </div>
    </form>
  );
};
